export type DistanceUnit = "km" | "mi";
export type DistanceDisplayUnit = "km" | "mi" | "m" | "yd";

const METERS_PER_KM = 1000;
const METERS_PER_MILE = 1609.344;
const METERS_PER_YARD = 0.9144;

export const parseTimeToSeconds = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes(":")) {
    const parts = trimmed.split(":").map((part) => Number(part || "0"));
    if (parts.some((part) => Number.isNaN(part))) return null;
    if (parts.length === 3) {
      const [hours, minutes, seconds] = parts;
      return Math.max(
        0,
        Math.floor(
          hours * 3600 + minutes * 60 + Math.min(seconds, 59)
        )
      );
    }
    const [minutes, seconds] = parts;
    return Math.max(0, Math.floor(minutes * 60 + Math.min(seconds, 59)));
  }
  const numeric = Number(trimmed);
  if (Number.isNaN(numeric)) return null;
  return Math.max(0, Math.floor(numeric));
};

export const formatTimeSeconds = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return "";
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const metersToDistanceUnit = (
  meters: number,
  unit: DistanceUnit
) => {
  if (unit === "mi") {
    return meters / METERS_PER_MILE;
  }
  return meters / METERS_PER_KM;
};

export const distanceUnitToMeters = (
  value: number,
  unit: DistanceUnit
) => {
  if (unit === "mi") {
    return value * METERS_PER_MILE;
  }
  return value * METERS_PER_KM;
};

export const getDistanceDisplay = (
  trackingType: string | null | undefined,
  unit: DistanceUnit
) => {
  if (trackingType === "distance_short") {
    return unit === "mi"
      ? { label: "yd" as DistanceDisplayUnit, metersPerUnit: METERS_PER_YARD }
      : { label: "m" as DistanceDisplayUnit, metersPerUnit: 1 };
  }
  return unit === "mi"
    ? { label: "mi" as DistanceDisplayUnit, metersPerUnit: METERS_PER_MILE }
    : { label: "km" as DistanceDisplayUnit, metersPerUnit: METERS_PER_KM };
};

export const metersToDistanceDisplay = (
  meters: number,
  trackingType: string | null | undefined,
  unit: DistanceUnit
) => {
  const display = getDistanceDisplay(trackingType, unit);
  return meters / display.metersPerUnit;
};

export const distanceDisplayToMeters = (
  value: number,
  trackingType: string | null | undefined,
  unit: DistanceUnit
) => {
  const display = getDistanceDisplay(trackingType, unit);
  return value * display.metersPerUnit;
};

export const formatDistance = (
  meters?: number | null,
  unit: DistanceUnit = "km",
  trackingType?: string | null
) => {
  if (meters === null || meters === undefined || Number.isNaN(meters)) {
    return "";
  }
  const display = getDistanceDisplay(trackingType, unit);
  const value = meters / display.metersPerUnit;
  const rounded = Math.round(value * 100) / 100;
  return `${rounded} ${display.label}`;
};
